import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Divider,
  Grid,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Tooltip,
  Button,
} from '@mui/material';
import SearchIcon      from '@mui/icons-material/Search';
import ClearIcon       from '@mui/icons-material/Clear';
import FilterListIcon  from '@mui/icons-material/FilterList';
import UpdateIcon      from '@mui/icons-material/Update';
import AddIcon         from '@mui/icons-material/Add';
import DeleteIcon      from '@mui/icons-material/Delete';
import DownloadIcon    from '@mui/icons-material/Download';
import EditNoteIcon    from '@mui/icons-material/EditNote';
import { authGet } from '../../services/apiService';
import GuardrailAnnotationDrawer from './GuardrailAnnotationDrawer';

const NAVY   = '#12365a';
const RED    = '#b91c1c';
const AMBER  = '#b45309';
const GREEN  = '#059669';
const BLUE   = '#1769aa';
const PURPLE = '#7c3aed';

const STRATEGIC_COLORS = {
  none:     '#9CA3AF',
  low:      GREEN,
  medium:   BLUE,
  high:     AMBER,
  critical: RED,
};

const PRESET_PROGRAMMES = ['White Glove', 'Test Case', 'At Risk', 'Strategic Partner'];

const TIER_META = {
  1: { label: 'T1', desc: 'Action Blocked',   color: RED,   bg: '#fef2f2', border: '#fecaca' },
  2: { label: 'T2', desc: 'Adjustable Limit', color: AMBER, bg: '#fffbeb', border: '#fde68a' },
  3: { label: 'T3', desc: 'UI Warning',        color: GREEN, bg: '#ecfdf5', border: '#a7f3d0' },
};

const TIER_CHIP_COLORS = { All: NAVY, 1: RED, 2: AMBER, 3: GREEN };

const BAND_STYLES = {
  Critical: { bg: '#fef2f2', color: RED,   border: '#fecaca' },
  High:     { bg: '#fffbeb', color: AMBER, border: '#fde68a' },
  Medium:   { bg: '#ecfdf5', color: GREEN, border: '#a7f3d0' },
  Low:      { bg: '#edf6fc', color: BLUE,  border: '#b9d9ee' },
};

const CLIENT_BAND_COLORS = { All: NAVY, Critical: RED, High: AMBER, Medium: GREEN, Low: BLUE };

const CLIENT_BAND_RANGES = {
  Critical: { minPoints: '1001' },
  High:     { minPoints: '501',  maxPoints: '1000' },
  Medium:   { minPoints: '101',  maxPoints: '500' },
  Low:      { maxPoints: '100' },
};

const COLUMNS = [
  { key: 'clientName',                 label: 'Client Name' },
  { key: 'clientUrl',                  label: 'URL' },
  { key: 'clientInstanceName',         label: 'Instance' },
  { key: 'dataCenter',                 label: 'Data Center' },
  { key: 'appServer',                  label: 'App Server' },
  { key: 'dbServer',                   label: 'DB Server' },
  { key: 'sameMachineClientCount',     label: 'Same Machine' },
  { key: 'totalSites',                 label: 'Sites' },
  { key: 'totalArchivedSites',         label: 'Archived' },
  { key: 'totalMySites',               label: 'My Sites' },
  { key: 'maxFoldersInSite',           label: 'Folders/Site' },
  { key: 'maxDocumentsInSite',         label: 'Docs/Site' },
  { key: 'maxDocumentsInFolder',       label: 'Docs/Folder' },
  { key: 'maxUsersInSite',             label: 'Users/Site' },
  { key: 'maxUsersInGroup',            label: 'Users/Group' },
  { key: 'maxGroupsInSite',            label: 'Groups/Site' },
  { key: 'maxISheetColumnsInSite',     label: 'iSheet Cols' },
  { key: 'maxISheetViewsInSite',       label: 'iSheet Views' },
  { key: 'maxRecordsInISheet',         label: 'Records/iSheet' },
  { key: 'maxStorageInSiteGB',         label: 'Storage/Site GB' },
  { key: 'totalSiteGroups',            label: 'Site Groups' },
  { key: 'totalSystemGroups',          label: 'System Groups' },
  { key: 'totalISheets',               label: 'iSheets' },
  { key: 'totalDocuments',             label: 'Documents' },
  { key: 'totalFolders',               label: 'Folders' },
  { key: 'totalLinkedISheets',         label: 'Linked iSheets' },
  { key: 'totalClientApiRegistrations', label: 'API Registrations' },
  { key: 'totalInternalUsers',         label: 'Internal Users' },
  { key: 'totalExternalUsers',         label: 'External Users' },
  { key: 'totalStorageGB',             label: 'Storage GB' },
  { key: 'totalApiCallsDatadog',        label: 'API Calls (DD)' },
  { key: 'totalMonthlyApiCallsDatadog', label: 'Monthly API Calls (DD)' },
  { key: 'totalWorkflows',             label: 'Workflows' },
  { key: 'totalWorkflowRules',         label: 'Wflow Rules' },
  { key: 'totalISheetTemplates',       label: 'iSheet Templates' },
];

const TEXT_COLS = new Set(['clientName', 'clientUrl', 'clientInstanceName', 'dataCenter', 'appServer', 'dbServer']);

const CLASSIFICATION_GROUPS = {
  Overall:     new Set(['totalSites', 'totalArchivedSites', 'totalMySites', 'totalWorkflows', 'totalWorkflowRules', 'totalSiteGroups', 'totalSystemGroups', 'totalISheets', 'totalDocuments', 'totalFolders', 'totalLinkedISheets', 'totalClientApiRegistrations', 'totalInternalUsers', 'totalExternalUsers', 'totalStorageGB', 'totalISheetTemplates']),
  'Per Site':  new Set(['maxFoldersInSite', 'maxDocumentsInSite', 'maxDocumentsInFolder', 'maxUsersInSite', 'maxUsersInGroup', 'maxGroupsInSite', 'maxISheetColumnsInSite', 'maxISheetViewsInSite', 'maxRecordsInISheet', 'maxStorageInSiteGB']),
  Operational: new Set(['totalApiCallsDatadog', 'totalMonthlyApiCallsDatadog']),
};

// Any column not in a classification group (Client Info columns) is always visible
const CLASSIFIED_COLS = new Set([
  ...CLASSIFICATION_GROUPS.Overall,
  ...CLASSIFICATION_GROUPS['Per Site'],
  ...CLASSIFICATION_GROUPS.Operational,
]);

const FILTER_COLS = [
  { key: 'points',                      label: 'Points',              type: 'number' },
  ...COLUMNS.map(c => ({ key: c.key, label: c.label, type: TEXT_COLS.has(c.key) ? 'text' : 'number' })),
];

const TEXT_OPS    = [{ v: 'contains', l: 'Contains' }, { v: 'equals', l: 'Equals' }, { v: 'startsWith', l: 'Starts with' }];
const NUMERIC_OPS = [{ v: 'eq', l: '=' }, { v: 'gt', l: '>' }, { v: 'gte', l: '≥' }, { v: 'lt', l: '<' }, { v: 'lte', l: '≤' }, { v: 'between', l: 'Between' }];

let _cfId = 0;
const newCfId = () => `cf${++_cfId}`;

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function getPointsBand(points) {
  if (points == null) return null;
  if (points > 1000) return 'Critical';
  if (points > 500)  return 'High';
  if (points > 100)  return 'Medium';
  return 'Low';
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

const toCompact = n => {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    const v = n / 1_000_000;
    return v >= 10 ? `${Math.floor(v)}M` : `${+(Math.round(v * 10) / 10)}M`;
  }
  if (abs >= 1_000) {
    const v = n / 1_000;
    return v >= 10 ? `${Math.floor(v)}K` : `${+(Math.round(v * 10) / 10)}K`;
  }
  return null;
};

const formatNum = v => {
  if (v == null || v === '') return '—';
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (isNaN(n)) return String(v);
  const int = Math.floor(n);
  const compact = toCompact(int);
  return compact ? `${int.toLocaleString()} (${compact})` : int.toLocaleString();
};

const formatStorage = v => {
  if (v == null || v === '') return '—';
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (isNaN(n)) return String(v);
  if (n >= 1000) return `${+(n / 1000).toFixed(2)} TB`;
  return `${+n.toFixed(2)} GB`;
};

const STORAGE_COLS = new Set(['maxStorageInSiteGB', 'totalStorageGB']);

const STAT_CARDS = [
  { key: 'total', label: 'Total Clients',  sub: 'All',              color: BLUE },
  { key: 'tier1', label: 'Tier 1',         sub: 'Action Blocked',   color: RED },
  { key: 'tier2', label: 'Tier 2',         sub: 'Adjustable Limit', color: AMBER },
  { key: 'tier3', label: 'Tier 3',         sub: 'UI Warning',       color: GREEN },
];

const HEADER_SX = {
  bgcolor: '#f6f8fb',
  color: '#6d788c',
  fontWeight: 700,
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  whiteSpace: 'nowrap',
  py: 1,
  px: 1.25,
};

const SORT_LABEL_SX = {
  color: '#6d788c !important',
  '& .MuiTableSortLabel-icon': { color: '#6d788c !important' },
  '&.Mui-active': { color: `${NAVY} !important` },
  '&.Mui-active .MuiTableSortLabel-icon': { color: `${NAVY} !important` },
};

const ACTIVE_CHIP_SX = {
  bgcolor: `${NAVY}08`,
  color: NAVY,
  border: `1px solid ${NAVY}22`,
  height: 24,
  fontSize: '0.72rem',
};

export default function GuardrailDashboardTab({ refreshKey = 0 }) {
  const [summary, setSummary] = useState({ total: 0, tier1: 0, tier2: 0, tier3: 0 });
  const emptyRanges = () => ({ critical: 0, high: 0, medium: 0, low: 0 });
  const [tierPointRanges, setTierPointRanges] = useState({ tier1: emptyRanges(), tier2: emptyRanges(), tier3: emptyRanges() });
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [dataCenters, setDataCenters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [lastUpload, setLastUpload] = useState(null);

  const [search, setSearch] = useState('');
  const [dcFilter, setDcFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [classificationFilter, setClassificationFilter] = useState('');
  const [clientBandFilter, setClientBandFilter] = useState('');
  const [programmeFilter, setProgrammeFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);
  const [sortBy, setSortBy] = useState('points');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [annotations, setAnnotations]       = useState({});  // { clientUrl: annotation }
  const [drawerOpen, setDrawerOpen]         = useState(false);
  const [drawerClient, setDrawerClient]     = useState(null);

  const debouncedSearch = useDebounce(search, 300);
  const debouncedColFilters = useDebounce(columnFilters, 400);

  const resetPage = useCallback(() => setPage(0), []);

  useEffect(() => { resetPage(); }, [debouncedSearch, dcFilter, tierFilter, clientBandFilter, programmeFilter, debouncedColFilters, sortBy, order, resetPage]);

  // Load all annotations once (and after every save/delete via refreshKey)
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch('/api/guardrail/annotations', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(json => {
        if (json.success) {
          const map = {};
          (json.data || []).forEach(a => { map[a.clientUrl] = a; });
          setAnnotations(map);
        }
      })
      .catch(() => {});
  }, [refreshKey]);

  useEffect(() => {
    setLoading(true);
    authGet('/api/guardrail/dashboard', null, 'json', { useCache: false })
      .then(data => {
        setSummary(data.summary || { total: 0, tier1: 0, tier2: 0, tier3: 0 });
        setDataCenters(data.dataCenters || []);
setLastUpload(data.lastUpload || null);
        const empty = () => ({ critical: 0, high: 0, medium: 0, low: 0 });
        setTierPointRanges(data.tierPointRanges || { tier1: empty(), tier2: empty(), tier3: empty() });
      })
      .catch(err => setError(err?.message || 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  const abortRef = useRef(null);

  useEffect(() => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setTableLoading(true);

    const readyCF = debouncedColFilters.filter(
      cf => cf.column && cf.operator && cf.value !== '' && cf.value != null
    );

    const bandRange = clientBandFilter ? CLIENT_BAND_RANGES[clientBandFilter] : null;

    const params = {
      search: debouncedSearch,
      dataCenter: dcFilter,
      sortBy,
      order,
      limit: rowsPerPage,
      skip: page * rowsPerPage,
    };
    if (tierFilter !== '') params.tier = tierFilter;
    if (bandRange?.minPoints) params.minPoints = bandRange.minPoints;
    if (bandRange?.maxPoints) params.maxPoints = bandRange.maxPoints;
    if (readyCF.length > 0) params.columnFilters = JSON.stringify(readyCF);
    if (programmeFilter) params.programme = programmeFilter;

    authGet('/api/guardrail/clients-with-tier', params, 'json', { useCache: false })
      .then(data => {
        if (!controller.signal.aborted) {
          setClients(data.clients || []);
          setTotal(data.total || 0);
        }
      })
      .catch(err => {
        if (!controller.signal.aborted) setError(err?.message || 'Failed to load clients');
      })
      .finally(() => {
        if (!controller.signal.aborted) setTableLoading(false);
      });

    return () => controller.abort();
  }, [debouncedSearch, dcFilter, tierFilter, clientBandFilter, programmeFilter, debouncedColFilters, sortBy, order, page, rowsPerPage]);

  const handleSort = col => {
    if (sortBy === col) {
      setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setOrder('desc');
    }
  };

  const handleTierChip = val => setTierFilter(prev => (prev === val ? '' : val));
  const handleClassificationChip = val => setClassificationFilter(prev => (prev === val ? '' : val));
  const handleClientBandChip = val => setClientBandFilter(prev => (prev === val ? '' : val));
  const handleProgrammeChip = val => setProgrammeFilter(prev => (prev === val ? '' : val));

  const handleSaveAnnotation = async (clientUrl, clientName, data) => {
    const token = localStorage.getItem('token');
    const res = await fetch('/api/guardrail/annotations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ clientUrl, clientName, ...data }),
    });
    const json = await res.json();
    if (!json.success) throw new Error(json.message || 'Save failed');
    setAnnotations(prev => ({ ...prev, [clientUrl]: json.data }));
  };

  const handleDeleteAnnotation = async (annotationId) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`/api/guardrail/annotations/${annotationId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    const json = await res.json();
    if (!json.success) throw new Error(json.message || 'Delete failed');
    setAnnotations(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(k => { if (next[k]._id === annotationId) delete next[k]; });
      return next;
    });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const readyCF = columnFilters.filter(cf => cf.column && cf.operator && cf.value !== '' && cf.value != null);
      const bandRange = clientBandFilter ? CLIENT_BAND_RANGES[clientBandFilter] : null;
      const params = { search, dataCenter: dcFilter, sortBy, order };
      if (tierFilter !== '') params.tier = tierFilter;
      if (bandRange?.minPoints) params.minPoints = bandRange.minPoints;
      if (bandRange?.maxPoints) params.maxPoints = bandRange.maxPoints;
      if (readyCF.length > 0) params.columnFilters = JSON.stringify(readyCF);
      if (programmeFilter) params.programme = programmeFilter;

      const qs = new URLSearchParams(params).toString();
      const blob = await authGet(`/api/guardrail/export?${qs}`, null, 'blob', { useCache: false });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `guardrail_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const addColumnFilter = () => {
    setColumnFilters(prev => [...prev, { id: newCfId(), column: FILTER_COLS[0].key, operator: 'contains', value: '', value2: '' }]);
  };

  const updateColumnFilter = (id, patch) => {
    setColumnFilters(prev => prev.map(cf => {
      if (cf.id !== id) return cf;
      const next = { ...cf, ...patch };
      // reset operator when column type changes
      if (patch.column && patch.column !== cf.column) {
        const isText = TEXT_COLS.has(patch.column);
        next.operator = isText ? 'contains' : 'eq';
        next.value = '';
        next.value2 = '';
      }
      return next;
    }));
  };

  const removeColumnFilter = id => setColumnFilters(prev => prev.filter(cf => cf.id !== id));

  const hasActiveFilters = !!(debouncedSearch || dcFilter || tierFilter || clientBandFilter || programmeFilter || columnFilters.length > 0);

  const activeFilterCount = [
    debouncedSearch ? 1 : 0,
    dcFilter ? 1 : 0,
    tierFilter ? 1 : 0,
    clientBandFilter ? 1 : 0,
    programmeFilter ? 1 : 0,
    columnFilters.length > 0 ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const clearAll = () => {
    setSearch('');
    setDcFilter('');
    setTierFilter('');
    setClassificationFilter('');
    setClientBandFilter('');
    setProgrammeFilter('');
    setColumnFilters([]);
  };

  // Computed from loaded annotations (all clients, not just current page)
  const strategicCount = Object.values(annotations).filter(
    a => a.strategicImportance === 'high' || a.strategicImportance === 'critical'
  ).length;
  const whiteGloveCount = Object.values(annotations).filter(
    a => a.programmes?.includes('White Glove')
  ).length;

  const visibleColumns = classificationFilter
    ? COLUMNS.filter(col => !CLASSIFIED_COLS.has(col.key) || CLASSIFICATION_GROUPS[classificationFilter]?.has(col.key))
    : COLUMNS;

  const STAT_CARD_STYLES = {
    total:     { bgcolor: '#edf6fc', color: BLUE,   border: '#b9d9ee' },
    tier1:     { bgcolor: '#fef2f2', color: RED,    border: '#fecaca' },
    tier2:     { bgcolor: '#fffbeb', color: AMBER,  border: '#fde68a' },
    tier3:     { bgcolor: '#ecfdf5', color: GREEN,  border: '#a7f3d0' },
    strategic: { bgcolor: '#f5f3ff', color: PURPLE, border: '#ddd6fe' },
  };

  return (
    <>
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      {error && (
        <Box mb={1}>
          <Alert severity="error" onClose={() => setError('')} sx={{ py: 0.5 }}>
            {error}
          </Alert>
        </Box>
      )}

      {lastUpload?.uploadedAt && (
        <Box mb={1} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <UpdateIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            Last updated: {formatDate(lastUpload.uploadedAt)}&nbsp;&middot;&nbsp;by {lastUpload.uploadedBy || '—'}
          </Typography>
        </Box>
      )}

      <Box mb={1.5}>
        <Paper elevation={0} sx={{ p: 1, border: '1px solid #dfe5ee', borderRadius: '12px', bgcolor: '#fff', boxShadow: '0 2px 12px rgba(30,55,80,.07)' }}>
          {loading ? (
            <Box display="flex" justifyContent="center" my={1.5}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {STAT_CARDS.map(({ key, label }) => {
                const style = STAT_CARD_STYLES[key];
                const isTier = key !== 'total';
                const pct = isTier && summary.total > 0 ? Math.round((summary[key] / summary.total) * 100) : null;
                const ranges = isTier ? tierPointRanges[key] : null;
                const tierCount = summary[key] ?? 0;
                const pctOf = n => tierCount > 0 ? Math.round((n / tierCount) * 100) : 0;
                const bands = [
                  { lbl: 'Critical >1K', val: ranges?.critical },
                  { lbl: 'High >500',    val: ranges?.high },
                  { lbl: 'Medium >100',  val: ranges?.medium },
                  { lbl: 'Low ≤100',     val: ranges?.low },
                ];
                return (
                  <Paper
                    key={key}
                    elevation={0}
                    sx={{
                      p: 1, minWidth: 130, flex: 1, bgcolor: style.bgcolor,
                      borderRadius: '10px', border: `1px solid ${style.border}`,
                      boxShadow: '0 1px 6px rgba(30,55,80,.05)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.75 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.1, color: style.color }}>
                        {tierCount}
                      </Typography>
                      {pct !== null && (
                        <Typography sx={{ color: style.color, fontWeight: 600, fontSize: '0.7rem' }}>
                          {pct}%
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#6d788c', lineHeight: 1.4 }}>
                      {label}
                    </Typography>
                    {ranges && tierCount > 0 && (
                      <Box sx={{ mt: 0.5, pt: 0.5, borderTop: `1px solid ${style.border}`, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px 4px' }}>
                        {bands.map(({ lbl, val }) => (
                          <Box key={lbl} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{ color: '#6d788c', fontSize: '0.62rem', whiteSpace: 'nowrap' }}>{lbl}</Typography>
                            <Typography sx={{ color: style.color, fontWeight: 700, fontSize: '0.62rem', ml: 0.5 }}>
                              {pctOf(val)}%
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>
                );
              })}

              {/* Strategic qualitative card */}
              <Paper
                elevation={0}
                sx={{
                  p: 1, minWidth: 130, flex: 1,
                  bgcolor: STAT_CARD_STYLES.strategic.bgcolor,
                  borderRadius: '10px',
                  border: `1px solid ${STAT_CARD_STYLES.strategic.border}`,
                  boxShadow: '0 1px 6px rgba(30,55,80,.05)',
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.1, color: PURPLE, textAlign: 'center' }}>
                  {strategicCount}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#6d788c', lineHeight: 1.4 }}>
                  Strategic
                </Typography>
                {strategicCount > 0 && (
                  <Box sx={{ mt: 0.5, pt: 0.5, borderTop: `1px solid ${STAT_CARD_STYLES.strategic.border}` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ color: '#6d788c', fontSize: '0.62rem' }}>White Glove</Typography>
                      <Typography sx={{ color: PURPLE, fontWeight: 700, fontSize: '0.62rem' }}>{whiteGloveCount}</Typography>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Box>
          )}
        </Paper>
      </Box>

      <Box mb={1.5}>
        <Paper elevation={0} sx={{ p: 2, border: '1px solid #dfe5ee', borderRadius: '12px', bgcolor: '#fff', boxShadow: '0 2px 12px rgba(30,55,80,.07)' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.7rem', color: '#6d788c' }}>Filters</Typography>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                size="small"
                placeholder="Search clients…"
                value={search}
                onChange={e => { setSearch(e.target.value); }}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: '#9CA3AF' }} />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch('')}>
                        <ClearIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>Data Center</InputLabel>
                <Select
                  value={dcFilter}
                  label="Data Center"
                  onChange={e => setDcFilter(e.target.value)}
                >
                  <MenuItem value="">All DCs</MenuItem>
                  {dataCenters.map(dc => (
                    <MenuItem key={dc} value={dc}>{dc}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Tier:
                </Typography>
                {['All', '1', '2', '3'].map(t => {
                  const isAll = t === 'All';
                  const active = isAll ? tierFilter === '' : tierFilter === t;
                  const color = isAll ? NAVY : TIER_CHIP_COLORS[parseInt(t, 10)];
                  return (
                    <Chip
                      key={t}
                      label={isAll ? 'All' : `T${t}`}
                      size="small"
                      onClick={() => (isAll ? (setTierFilter('')) : handleTierChip(t))}
                      sx={{
                        height: 26,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        bgcolor: active ? color : 'transparent',
                        color: active ? '#fff' : color,
                        border: `1.5px solid ${color}`,
                        '&:hover': { bgcolor: active ? color : `${color}18` },
                      }}
                    />
                  );
                })}
              </Box>
            </Grid>

            {activeFilterCount > 0 && (
              <Grid item xs={12} sm={6} md={2}>
                <Button
                  size="small"
                  onClick={clearAll}
                  sx={{
                    color: RED,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    minWidth: 0,
                    px: 1,
                  }}
                >
                  Clear all ({activeFilterCount})
                </Button>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Classification:
                </Typography>
                {['All', 'Overall', 'Per Site', 'Operational'].map(cls => {
                  const isAll = cls === 'All';
                  const active = isAll ? classificationFilter === '' : classificationFilter === cls;
                  return (
                    <Chip
                      key={cls}
                      label={cls}
                      size="small"
                      onClick={() => (isAll ? setClassificationFilter('') : handleClassificationChip(cls))}
                      sx={{
                        height: 26,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        bgcolor: active ? BLUE : 'transparent',
                        color: active ? '#fff' : BLUE,
                        border: `1.5px solid ${BLUE}`,
                        '&:hover': { bgcolor: active ? BLUE : `${BLUE}18` },
                      }}
                    />
                  );
                })}
              </Box>
            </Grid>

            <Grid item xs={12} sm={8} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Client:
                </Typography>
                {['All', 'Critical', 'High', 'Medium', 'Low'].map(band => {
                  const isAll = band === 'All';
                  const active = isAll ? clientBandFilter === '' : clientBandFilter === band;
                  const color = CLIENT_BAND_COLORS[band];
                  return (
                    <Chip
                      key={band}
                      label={band}
                      size="small"
                      onClick={() => (isAll ? setClientBandFilter('') : handleClientBandChip(band))}
                      sx={{
                        height: 26,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        bgcolor: active ? color : 'transparent',
                        color: active ? '#fff' : color,
                        border: `1.5px solid ${color}`,
                        '&:hover': { bgcolor: active ? color : `${color}18` },
                      }}
                    />
                  );
                })}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Programme:
                </Typography>
                {['All', ...PRESET_PROGRAMMES].map(prog => {
                  const isAll = prog === 'All';
                  const active = isAll ? programmeFilter === '' : programmeFilter === prog;
                  return (
                    <Chip
                      key={prog}
                      label={prog}
                      size="small"
                      onClick={() => (isAll ? setProgrammeFilter('') : handleProgrammeChip(prog))}
                      sx={{
                        height: 26,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        bgcolor: active ? PURPLE : 'transparent',
                        color:   active ? '#fff' : PURPLE,
                        border:  `1.5px solid ${PURPLE}`,
                        '&:hover': { bgcolor: active ? PURPLE : `${PURPLE}18` },
                      }}
                    />
                  );
                })}
              </Box>
            </Grid>

          </Grid>

          <Divider sx={{ my: 1.5 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.7rem', color: '#6d788c' }}>Column Filters</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addColumnFilter} variant="outlined" sx={{ borderColor: NAVY, color: NAVY, '&:hover': { borderColor: NAVY, bgcolor: '#edf6fc' } }}>
              Add filter
            </Button>
          </Box>

          {columnFilters.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No column filters applied. Click "Add filter" to filter on any column.
            </Typography>
          )}

          {columnFilters.map(cf => {
            const colDef = FILTER_COLS.find(c => c.key === cf.column) || FILTER_COLS[0];
            const isText = colDef.type === 'text';
            const ops = isText ? TEXT_OPS : NUMERIC_OPS;
            return (
              <Box key={cf.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Column</InputLabel>
                  <Select
                    value={cf.column}
                    label="Column"
                    onChange={e => updateColumnFilter(cf.id, { column: e.target.value })}
                  >
                    {FILTER_COLS.map(c => (
                      <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Operator</InputLabel>
                  <Select
                    value={cf.operator}
                    label="Operator"
                    onChange={e => updateColumnFilter(cf.id, { operator: e.target.value })}
                  >
                    {ops.map(op => (
                      <MenuItem key={op.v} value={op.v}>{op.l}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  label={cf.operator === 'between' ? 'From' : 'Value'}
                  value={cf.value}
                  onChange={e => updateColumnFilter(cf.id, { value: e.target.value })}
                  type={isText ? 'text' : 'number'}
                  sx={{ width: 130 }}
                />

                {cf.operator === 'between' && (
                  <TextField
                    size="small"
                    label="To"
                    value={cf.value2}
                    onChange={e => updateColumnFilter(cf.id, { value2: e.target.value })}
                    type="number"
                    sx={{ width: 130 }}
                  />
                )}

                <Tooltip title="Remove filter">
                  <IconButton size="small" onClick={() => removeColumnFilter(cf.id)} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
        </Paper>
      </Box>

      {hasActiveFilters && (
        <Box mb={1} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 600 }}>
            Active:
          </Typography>
          {debouncedSearch && (
            <Chip
              label={`Search: "${debouncedSearch}"`}
              size="small"
              onDelete={() => setSearch('')}
              sx={ACTIVE_CHIP_SX}
            />
          )}
          {dcFilter && (
            <Chip
              label={`DC: ${dcFilter}`}
              size="small"
              onDelete={() => setDcFilter('')}
              sx={ACTIVE_CHIP_SX}
            />
          )}
          {tierFilter && (
            <Chip
              label={`Tier: T${tierFilter}`}
              size="small"
              onDelete={() => setTierFilter('')}
              sx={ACTIVE_CHIP_SX}
            />
          )}
          {classificationFilter && (
            <Chip
              label={`Classification: ${classificationFilter}`}
              size="small"
              onDelete={() => setClassificationFilter('')}
              sx={ACTIVE_CHIP_SX}
            />
          )}
          {clientBandFilter && (
            <Chip
              label={`Client: ${clientBandFilter}`}
              size="small"
              onDelete={() => setClientBandFilter('')}
              sx={ACTIVE_CHIP_SX}
            />
          )}
          {programmeFilter && (
            <Chip
              label={`Programme: ${programmeFilter}`}
              size="small"
              onDelete={() => setProgrammeFilter('')}
              sx={ACTIVE_CHIP_SX}
            />
          )}
          {columnFilters.map(cf => {
            const colDef = FILTER_COLS.find(c => c.key === cf.column);
            const label = colDef ? colDef.label : cf.column;
            const opLabel = [...TEXT_OPS, ...NUMERIC_OPS].find(o => o.v === cf.operator)?.l || cf.operator;
            const val = cf.operator === 'between' ? `${cf.value}–${cf.value2}` : cf.value;
            return (
              <Chip
                key={cf.id}
                label={`${label} ${opLabel} ${val}`}
                size="small"
                onDelete={() => removeColumnFilter(cf.id)}
                sx={ACTIVE_CHIP_SX}
              />
            );
          })}
        </Box>
      )}

      <Box mb={1.5}>
        <Paper elevation={0} sx={{ p: 1.5, position: 'relative', overflow: 'hidden', border: '1px solid #dfe5ee', borderRadius: '12px', bgcolor: '#fff', boxShadow: '0 2px 12px rgba(30,55,80,.07)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {tableLoading ? 'Loading…' : `${total.toLocaleString()} client${total !== 1 ? 's' : ''}`}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={exporting ? <CircularProgress size={14} /> : <DownloadIcon />}
              onClick={handleExport}
              disabled={exporting || total === 0}
              sx={{ borderColor: NAVY, color: NAVY, '&:hover': { borderColor: NAVY, bgcolor: '#edf6fc' }, '&.Mui-disabled': { borderColor: '#dfe5ee', color: '#6d788c' } }}
            >
              {exporting ? 'Exporting…' : 'Export to Excel'}
            </Button>
          </Box>

          {tableLoading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 10,
                bgcolor: 'rgba(255,255,255,0.7)',
                backdropFilter: 'blur(2px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress size={28} />
            </Box>
          )}
          <TableContainer sx={{ maxHeight: 600, border: '1px solid #dfe5ee', borderRadius: '8px' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...HEADER_SX, minWidth: 68 }}>
                    <TableSortLabel
                      active={sortBy === '_tier'}
                      direction={sortBy === '_tier' ? order : 'asc'}
                      onClick={() => handleSort('_tier')}
                      sx={SORT_LABEL_SX}
                    >
                      Tier
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ ...HEADER_SX, minWidth: 90 }}>
                    <TableSortLabel
                      active={sortBy === 'points'}
                      direction={sortBy === 'points' ? order : 'desc'}
                      onClick={() => handleSort('points')}
                      sx={SORT_LABEL_SX}
                    >
                      Points
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ ...HEADER_SX, minWidth: 120 }}>Qualitative</TableCell>
                  {visibleColumns.map(col => (
                    <TableCell key={col.key} sx={HEADER_SX}>
                      <TableSortLabel
                        active={sortBy === col.key}
                        direction={sortBy === col.key ? order : 'asc'}
                        onClick={() => handleSort(col.key)}
                        sx={SORT_LABEL_SX}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.length === 0 && !tableLoading ? (
                  <TableRow>
                    <TableCell colSpan={3 + visibleColumns.length} align="center" sx={{ py: 6 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                        <FilterListIcon sx={{ fontSize: 40, opacity: 0.3, color: '#374151' }} />
                        <Typography variant="body2" color="text.secondary">
                          No clients match the current filters.
                        </Typography>
                        {hasActiveFilters && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={clearAll}
                            sx={{ textTransform: 'none', fontSize: '0.78rem' }}
                          >
                            Clear filters
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  clients.map((row, idx) => {
                    const tier = row._tier;
                    const tierMeta = tier ? TIER_META[tier] : null;
                    const ann = annotations[row.clientUrl];
                    const bonus = ann?.bonusPoints || 0;
                    const effectivePoints = (row.points || 0) + bonus;
                    const band = getPointsBand(row.points != null ? effectivePoints : null);
                    const bandStyle = band ? BAND_STYLES[band] : null;
                    const rowBorderColor =
                      tier === 1 ? RED : tier === 2 ? AMBER : tier === 3 ? GREEN : 'transparent';

                    return (
                      <TableRow
                        key={row._id || idx}
                        sx={{
                          borderLeft: `3px solid ${rowBorderColor}`,
                          bgcolor: idx % 2 === 0 ? '#fbfcfe' : 'transparent',
                          '&:hover': { bgcolor: '#f1f7fb' },
                        }}
                      >
                        <TableCell sx={{ px: 1.25, py: 0.75, whiteSpace: 'nowrap' }}>
                          {tierMeta ? (
                            <Tooltip title={tierMeta.desc} arrow>
                              <Chip
                                label={tierMeta.label}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.72rem',
                                  fontWeight: 700,
                                  bgcolor: tierMeta.bg,
                                  color: tierMeta.color,
                                  border: `1px solid ${tierMeta.border}`,
                                }}
                              />
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" sx={{ color: '#9CA3AF' }}>—</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ px: 1.25, py: 0.75, whiteSpace: 'nowrap' }}>
                          {row.points != null ? (
                            <Tooltip
                              title={bonus > 0 ? `Base: ${row.points} + Bonus: ${bonus}${ann?.bonusReason ? ` (${ann.bonusReason})` : ''}` : ''}
                              arrow
                              disableHoverListener={bonus === 0}
                            >
                              <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 0.25 }}>
                                <Chip
                                  label={effectivePoints}
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: '0.72rem',
                                    fontWeight: 700,
                                    fontFamily: 'monospace',
                                    bgcolor: bandStyle ? bandStyle.bg : '#F3F4F6',
                                    color: bandStyle ? bandStyle.color : '#374151',
                                    border: `1px solid ${bandStyle ? bandStyle.border : '#D1D5DB'}`,
                                  }}
                                />
                                {bonus > 0 && (
                                  <Typography sx={{ fontSize: '0.62rem', color: PURPLE, fontWeight: 600, textAlign: 'center' }}>
                                    +{bonus} bonus
                                  </Typography>
                                )}
                              </Box>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" sx={{ color: '#9CA3AF' }}>—</Typography>
                          )}
                        </TableCell>

                        {/* Qualitative cell */}
                        {(() => {
                          const si = ann?.strategicImportance;
                          const progs = ann?.programmes || [];
                          const siColor = si && si !== 'none' ? STRATEGIC_COLORS[si] : null;
                          return (
                            <TableCell sx={{ px: 1.25, py: 0.75, whiteSpace: 'nowrap' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
                                {siColor && (
                                  <Chip
                                    label={si.charAt(0).toUpperCase() + si.slice(1)}
                                    size="small"
                                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: `${siColor}18`, color: siColor, border: `1px solid ${siColor}40` }}
                                  />
                                )}
                                {progs.slice(0, 2).map(p => (
                                  <Chip key={p} label={p} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: `${PURPLE}10`, color: PURPLE, border: `1px solid ${PURPLE}30` }} />
                                ))}
                                {progs.length > 2 && (
                                  <Typography variant="caption" sx={{ color: '#9CA3AF', fontSize: '0.65rem' }}>+{progs.length - 2}</Typography>
                                )}
                                <Tooltip title={ann ? 'Edit annotation' : 'Add annotation'} arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() => { setDrawerClient(row); setDrawerOpen(true); }}
                                    sx={{ p: 0.25, color: ann ? PURPLE : '#D1D5DB', '&:hover': { color: PURPLE } }}
                                  >
                                    <EditNoteIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          );
                        })()}

                        {visibleColumns.map(col => (
                          <TableCell
                            key={col.key}
                            sx={{
                              fontSize: '0.8rem',
                              color: '#374151',
                              whiteSpace: 'nowrap',
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              px: 1.25,
                              py: 0.75,
                            }}
                          >
                            {TEXT_COLS.has(col.key)
                            ? (row[col.key] != null && row[col.key] !== '' ? String(row[col.key]) : '—')
                            : STORAGE_COLS.has(col.key)
                              ? formatStorage(row[col.key])
                              : formatNum(row[col.key])}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{ borderTop: '1px solid rgba(0,0,0,0.08)' }}
          />
        </Paper>
      </Box>
    </Box>

    <GuardrailAnnotationDrawer
      open={drawerOpen}
      client={drawerClient}
      annotation={drawerClient ? annotations[drawerClient.clientUrl] || null : null}
      onSave={handleSaveAnnotation}
      onDelete={handleDeleteAnnotation}
      onClose={() => setDrawerOpen(false)}
    />
    </>
  );
}
